import {
  useState,
  useRef,
  createContext,
  Dispatch,
  SetStateAction,
  MutableRefObject,
  useEffect,
  useCallback
} from 'react'
import { useSelector } from 'react-redux'
import { AgentData, ChatBubbleItem, ChatRawMessageItem } from '../utils/types'
import { PendingFrontendToolCall } from '../frontend-tools/types'

interface ChatbotContextType {
  rawMessages: ChatRawMessageItem[]
  setRawMessages: Dispatch<SetStateAction<ChatRawMessageItem[]>>
  messages: ChatBubbleItem[]
  setMessages: Dispatch<SetStateAction<ChatBubbleItem[]>>
  currentChat: { id: any; title: any; recentlyUpdated?: any }
  setCurrentChat: Dispatch<SetStateAction<any>>
  assistants: AgentData[] | null
  setAssistants: Dispatch<SetStateAction<AgentData[] | null>>
  isStreaming: boolean
  setIsStreaming: Dispatch<SetStateAction<boolean>>
  chatOption: number
  setChatOption: Dispatch<SetStateAction<number>>
  controller: AbortController
  setController: Dispatch<SetStateAction<AbortController>>
  disableInput: boolean
  setDisableInput: Dispatch<SetStateAction<boolean>>
  streamReader: MutableRefObject<any>
  selectedMessage: any
  setSelectedMessage: Dispatch<SetStateAction<any>>
  fetchingMsgs: boolean
  setFetchingMsgs: Dispatch<SetStateAction<boolean>>
  speakingId: string | null | undefined
  setSpeakingId: Dispatch<SetStateAction<string | null | undefined>>
  attachments: { count: number; previewFile: any }
  setAttachments: Dispatch<SetStateAction<{ count: number; previewFile: any }>>
  barMode: { previousMode: any; active: any; files: any[]; source: any }
  setBarMode: Dispatch<
    SetStateAction<{ previousMode: any; active: any; files: any[]; source: any }>
  >
  chatMode: string
  setChatMode: Dispatch<SetStateAction<string>>
  dataProductsInChat: string[] | undefined
  setDataProductsInChat: Dispatch<SetStateAction<string[]>>
  // Raw threadMcpServers array for the current thread
  threadMcpServers: any[] | null
  setThreadMcpServers: Dispatch<SetStateAction<any[] | null>>
  // Raw personalDataBoxes array for the current thread - a sibling field to threadMcpServers,
  // not nested inside it
  threadPersonalDataBoxes: any[] | null
  setThreadPersonalDataBoxes: Dispatch<SetStateAction<any[] | null>>
  threadDataboxIds: string[]
  setThreadDataboxIds: Dispatch<SetStateAction<string[]>>
  threadDataProductIds: string[]
  setThreadDataProductIds: Dispatch<SetStateAction<string[]>>
  // File-level scope (cognitive search IDs) narrowed within a databox for this thread
  threadCognitiveSearchIds: string[]
  setThreadCognitiveSearchIds: Dispatch<SetStateAction<string[]>>
  // Data products narrowed/nested inside a databox for this thread
  threadDataProductsByDatabox: Array<{ dataProductId: string; databoxId: string }>
  setThreadDataProductsByDatabox: Dispatch<
    SetStateAction<Array<{ dataProductId: string; databoxId: string }>>
  >
  // Thread's metadata.Type from /api/thread/:id (e.g. 'snowflake') - identifies tool-only threads
  // whose responses never get a runId
  threadType: string | null
  setThreadType: Dispatch<SetStateAction<string | null>>
  thinkingTimeElapsed: number
  setThinkingTimeElapsed: Dispatch<SetStateAction<number>>
  isThinking: boolean
  setIsThinking: Dispatch<SetStateAction<boolean>>
  pendingFrontendToolCalls: PendingFrontendToolCall[]
  setPendingFrontendToolCalls: Dispatch<SetStateAction<PendingFrontendToolCall[]>>
  isWebSearchEnabled: boolean | null
  setIsWebSearchEnabled: Dispatch<SetStateAction<boolean | null>>
  selectedProject: SelectedProject | null // Project whose chat area is currently shown
  setSelectedProject: Dispatch<SetStateAction<SelectedProject | null>>
  projectsRefreshKey: number // Bumped to ask the sidebar to refetch its projects list
  triggerProjectsRefresh: () => void
  // Thread ids created/opened from within a project, mapped to their project. The sidebar
  // uses this to keep project chats out of the flat history list, and the chat header uses
  // it to show a project chip for the open conversation.
  projectThreadIdsRef: MutableRefObject<Map<string, SelectedProject>>
}

export interface SelectedProject {
  id: string
  name: string
  icon: string
  color: string
  instructions?: string
  memoryMode?: number
}

export const ChatbotContext = createContext<ChatbotContextType>({} as ChatbotContextType)

interface ChatbotProviderProps {
  children: React.ReactNode
}

const ChatbotProvider: React.FC<ChatbotProviderProps> = ({ children }) => {
  const { selectedAgent } = useSelector((state: any) => state.chatbotReducer)
  const [rawMessages, setRawMessages] = useState<ChatRawMessageItem[]>([])
  const [messages, setMessages] = useState<ChatBubbleItem[]>([])
  const [currentChat, setCurrentChat] = useState<any>({
    id: null,
    title: null
  })
  const [assistants, setAssistants] = useState<AgentData[] | null>(null)
  const [isStreaming, setIsStreaming] = useState(false)
  const [chatOption, setChatOption] = useState<number>(
    selectedAgent ? selectedAgent?.conversationStyle : 0.3
  )
  const [controller, setController] = useState(new AbortController())
  const [disableInput, setDisableInput] = useState(false)
  const streamReader = useRef(undefined)
  const [selectedMessage, setSelectedMessage] = useState<any>(null)
  const [fetchingMsgs, setFetchingMsgs] = useState(false)
  const [speakingId, setSpeakingId] = useState<string | null | undefined>(undefined)
  //Will check the need for below once image chat feature is implemented
  const [attachments, setAttachments] = useState<any>({ count: 0, previewFile: null })
  //Will check the need for below once smart data feature is implemented
  const [barMode, setBarMode] = useState<any>({
    previousMode: null,
    active: null,
    files: [],
    source: null
  })
  const [chatMode, setChatMode] = useState<string>('chat')
  const [dataProductsInChat, setDataProductsInChat] = useState<string[]>([])
  const [threadMcpServers, setThreadMcpServers] = useState<any[] | null>(null)
  const [threadPersonalDataBoxes, setThreadPersonalDataBoxes] = useState<any[] | null>(null)
  const [threadDataboxIds, setThreadDataboxIds] = useState<string[]>([])
  const [threadDataProductIds, setThreadDataProductIds] = useState<string[]>([])
  const [threadCognitiveSearchIds, setThreadCognitiveSearchIds] = useState<string[]>([])
  const [threadDataProductsByDatabox, setThreadDataProductsByDatabox] = useState<
    Array<{ dataProductId: string; databoxId: string }>
  >([])
  const [threadType, setThreadType] = useState<string | null>(null)
  const [thinkingTimeElapsed, setThinkingTimeElapsed] = useState<number>(0)
  const [isThinking, setIsThinking] = useState<boolean>(false)
  const [pendingFrontendToolCalls, setPendingFrontendToolCalls] = useState<
    PendingFrontendToolCall[]
  >([])
  const [isWebSearchEnabled, setIsWebSearchEnabled] = useState<boolean | null>(null)
  const [selectedProject, setSelectedProject] = useState<SelectedProject | null>(null)
  const [projectsRefreshKey, setProjectsRefreshKey] = useState<number>(0)
  const triggerProjectsRefresh = useCallback(() => setProjectsRefreshKey((key) => key + 1), [])
  const projectThreadIdsRef = useRef<Map<string, SelectedProject>>(new Map())

  return (
    <ChatbotContext
      value={{
        currentChat,
        setCurrentChat,
        assistants,
        setAssistants,
        messages,
        setMessages,
        rawMessages,
        setRawMessages,
        chatMode,
        setChatMode,
        barMode,
        setBarMode,
        isStreaming,
        setIsStreaming,
        chatOption,
        setChatOption,
        controller,
        setController,
        streamReader,
        disableInput,
        setDisableInput,
        fetchingMsgs,
        setFetchingMsgs,
        speakingId,
        setSpeakingId,
        selectedMessage,
        setSelectedMessage,
        attachments,
        setAttachments,
        dataProductsInChat,
        setDataProductsInChat,
        threadMcpServers,
        setThreadMcpServers,
        threadPersonalDataBoxes,
        setThreadPersonalDataBoxes,
        threadDataboxIds,
        setThreadDataboxIds,
        threadDataProductIds,
        setThreadDataProductIds,
        threadCognitiveSearchIds,
        setThreadCognitiveSearchIds,
        threadDataProductsByDatabox,
        setThreadDataProductsByDatabox,
        threadType,
        setThreadType,
        thinkingTimeElapsed,
        setThinkingTimeElapsed,
        isThinking,
        setIsThinking,
        pendingFrontendToolCalls,
        setPendingFrontendToolCalls,
        isWebSearchEnabled,
        setIsWebSearchEnabled,
        selectedProject,
        setSelectedProject,
        projectsRefreshKey,
        triggerProjectsRefresh,
        projectThreadIdsRef
      }}>
      {children}
    </ChatbotContext>
  )
}

export { ChatbotProvider }
